import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Category } from './Category';
import { createLazy, LoadingStatus, Lazy } from './lazy';
import { Post, Posts } from './Post';
import { RestAdminApi } from './RestAdminApi';
import { RestContentApi } from './RestContentApi';
import { AppThunk } from './store';

// XXX global scope.
const api = new RestContentApi('https://www.whoisnemos.com/api');
const adminApi = new RestAdminApi('https://www.whoisnemos.com/api');

export interface PostsCategory {
  posts: Lazy<Post[]>;
  map: { [key: string]: Lazy<Post> };
}

export interface PostsSlice {
  drawings: PostsCategory;
  others: PostsCategory;
  projects: PostsCategory;
  sketches: PostsCategory;
  tattoos: PostsCategory;
  walls: PostsCategory;
}

const EMPTY_CATEGORY: PostsCategory = { posts: createLazy(), map: {} };

const initialState: PostsSlice = {
  drawings: EMPTY_CATEGORY,
  others: EMPTY_CATEGORY,
  projects: EMPTY_CATEGORY,
  sketches: EMPTY_CATEGORY,
  tattoos: EMPTY_CATEGORY,
  walls: EMPTY_CATEGORY,
};

export const postsSlice = createSlice({
  name: 'posts',
  initialState,
  reducers: {
    setLoadingState(
      state: PostsSlice,
      action: PayloadAction<{ category: Category; status: LoadingStatus }>
    ) {
      const { category, status } = action.payload;
      state[category].posts.status = status;
    },
    storePosts(state: PostsSlice, action: PayloadAction<{ category: Category; posts: Posts }>) {
      const { category, posts } = action.payload;
      state[category].posts = {
        status: 'awake',
        item: posts.posts,
      };
      for (const post of posts.posts) {
        state[category].map[post.slug] = { status: 'awake', item: post };
      }
    },
    initPost(state: PostsSlice, action: PayloadAction<{ category: Category; slug: string }>) {
      const { category, slug } = action.payload;
      state[category].map = { ...state[category].map, [slug]: { status: 'awakening' } };
    },
    storePost(state: PostsSlice, action: PayloadAction<Post>) {
      const post = action.payload;
      state[post.category].map = {
        ...state[post.category].map,
        [post.slug]: { status: 'awake', item: post },
      };
    },
    storePostContent(
      state: PostsSlice,
      action: PayloadAction<{
        category: Category;
        slug: string;
        content: { en?: string; it?: string };
      }>
    ) {
      const { category, slug, content } = action.payload;

      switch (state[category].posts.status) {
        case 'awakening':
          throw new Error('Race condition');
        case 'sleeping':
          // Nothing to do.
          break;
        case 'dead':
          // Nothing to do.
          // XXX shouldn't happen.
          break;
        case 'awake':
          const categoryPost = state[category].posts.item.find((post) => post.slug === slug);
          if (categoryPost) {
            categoryPost.content.en = content.en ?? '';
            categoryPost.content.it = content.it ?? '';
          }
          break;
      }

      const mapPost = state[category].map[slug];
      if (mapPost) {
        switch (mapPost.status) {
          case 'awakening':
            throw new Error('Race condition');
          case 'sleeping':
            // Nothing to do.
            break;
          case 'dead':
            // Nothing to do.
            // XXX shouldn't happen.
            break;
          case 'awake':
            mapPost.item.content.en = content.en;
            mapPost.item.content.it = content.it;
            break;
        }
      }
    },
  },
});

export const fetchPosts =
  (category: Category): AppThunk =>
  async (dispatch, getState) => {
    if (getState().posts[category].posts.status !== 'sleeping') {
      return;
    }
    dispatch(postsSlice.actions.setLoadingState({ category, status: 'awakening' }));
    let posts: Posts;
    try {
      posts = await api.getPosts(category);
    } catch (e) {
      dispatch(postsSlice.actions.setLoadingState({ category, status: 'dead' }));
      dispatch(
        postsSlice.actions.storePosts({ category, posts: { posts: [], total: 0, totalPages: 0 } })
      );
    }
    dispatch(postsSlice.actions.storePosts({ category, posts }));
  };

export const fetchPost =
  (category: Category, slug: string): AppThunk =>
  async (dispatch, getState) => {
    const map = getState().posts[category].map;
    if (slug in map && map[slug].status !== 'sleeping') {
      return;
    }
    dispatch(postsSlice.actions.initPost({ category, slug }));
    let post: Post;
    try {
      post = await api.getPost(category, slug);
    } catch (e) {
      throw new Error(`Error fetching post: ${e}`);
    }
    dispatch(postsSlice.actions.storePost(post));
  };

export const updatePostContent =
  (category: Category, slug: string, content: { en?: string; it?: string }): AppThunk =>
  async (dispatch) => {
    content = await adminApi.updatePostContent(category, slug, content);
    dispatch(postsSlice.actions.storePostContent({ category, slug, content }));
  };

export function getCategoryPosts(slice: PostsSlice, category: Category): Lazy<Post[]> {
  return slice[category].posts;
}
export function getPost(slice: PostsSlice, category: Category, slug: string): Lazy<Post> {
  return slice[category].map[slug] || createLazy();
}
export function getPostNeighbors(
  slice: PostsSlice,
  category: Category,
  slug: string
): { prev: Lazy<Post>; next: Lazy<Post>; index: number | null; total: number | null } {
  if (slice[category].posts.status !== 'awake') {
    return { prev: createLazy(), next: createLazy(), index: null, total: null };
  }
  const index = slice[category].posts.item.findIndex((post) => post.slug === slug);
  const total = slice[category].posts.item.length;
  return {
    prev:
      index < 1
        ? { status: 'dead' }
        : { status: 'awake', item: slice[category].posts.item[index - 1] },
    next:
      index >= total - 1
        ? { status: 'dead' }
        : { status: 'awake', item: slice[category].posts.item[index + 1] },
    index,
    total,
  };
}
